import { useCallback, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { DocumentNode } from 'graphql';

import { AppendState, CurrencyMsg } from '../browser';
import { currency } from '../queries/__generated__/currency';

const selector = (s: AppendState) => s.currency;

export interface UseCurrencyApi<Q extends currency> {
    code: string | null;
    available: string[];
    setCurrency(code: string);
    data: Q | undefined;
}

const EMPTY_ARRAY = [];

export function useCurrencyCodes<Q extends currency>(query: DocumentNode, ssr = true): UseCurrencyApi<Q> {
    const { data, loading, error } = useQuery<Q>(query, { ssr });
    if (error) {
        console.error('An error occurred when fetching currency codes');
    }
    const dispatch = useDispatch();
    const { code } = useSelector(selector, shallowEqual);

    const setCurrency = useCallback(
        (code) => {
            dispatch(CurrencyMsg('Currency.Set', code));
        },
        [dispatch],
    );

    return useMemo(() => {
        const available: string[] = (data?.currency?.available_currency_codes as string[]) || EMPTY_ARRAY;
        return {
            setCurrency,
            code,
            loading,
            available,
            data,
        };
    }, [code, data, loading, setCurrency]);
}

export function useSetCurrency() {
    const dispatch = useDispatch();
    return useCallback(
        (code: string) => {
            dispatch(CurrencyMsg('Currency.Set', code));
        },
        [dispatch],
    );
}
